import React, { useState, useEffect } from 'react';
import { FiClock, FiCheckCircle, FiBook, FiPaperclip, FiSend, FiSave, FiX } from 'react-icons/fi';
import { studentAPI } from '../../services/api';
import { prepareHomeworkHtml } from '../../utils/homeworkContent';
import './StudentHomework.css';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

const StudentHomework = () => {
    const [homework, setHomework] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedHomework, setSelectedHomework] = useState(null);
    const [submission, setSubmission] = useState(null);
    const [submissionLoading, setSubmissionLoading] = useState(false);
    const [submissionText, setSubmissionText] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [saveDraftLoading, setSaveDraftLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [uploadingFile, setUploadingFile] = useState(false);

    useEffect(() => {
        loadHomework();
    }, []);

    const loadHomework = async () => {
        try {
            setLoading(true);
            const response = await studentAPI.getMyHomework();
            setHomework(response.data.homework || []);
        } catch (error) {
            console.error('Ошибка загрузки домашних заданий:', error);
        } finally {
            setLoading(false);
        }
    };
    const loadSubmission = async (homeworkId) => {
        setSubmissionLoading(true);
        setSubmission(null);
        setSubmissionText('');
        setAttachments([]);
        try {
            const res = await studentAPI.getHomeworkSubmission(homeworkId);
            const sub = res.data.submission;
            setSubmission(sub || null);
            if (sub) {
                setSubmissionText(sub.submission_text || '');
                setAttachments(Array.isArray(sub.attachments) ? sub.attachments : []);
            }
        } catch (e) {
            console.error('Ошибка загрузки ответа:', e);
        } finally {
            setSubmissionLoading(false);
        }
    };

    const handleOpenHomework = async (hw) => {
        setSelectedHomework(hw);
        loadSubmission(hw.id);
        
        // Отмечаем как прочитанное
        if (!hw.is_read) {
            try {
                await studentAPI.markHomeworkAsRead(hw.id);
                setHomework(homework.map(h => 
                    h.id === hw.id ? { ...h, is_read: true, read_at: new Date() } : h
                ));
            } catch (error) {
                console.error('Ошибка отметки ДЗ:', error);
            }
        }
    };

    const handleCloseHomework = () => {
        setSelectedHomework(null);
        setSubmission(null);
        setSubmissionText('');
        setAttachments([]);
    };

    const handleSaveDraft = async () => {
        if (!selectedHomework) return;
        setSaveDraftLoading(true);
        try {
            await studentAPI.saveHomeworkSubmission(selectedHomework.id, {
                submission_text: submissionText,
                attachments
            });
            await loadSubmission(selectedHomework.id);
        } catch (e) {
            console.error(e);
            alert(e.response?.data?.error || 'Не удалось сохранить черновик');
        } finally {
            setSaveDraftLoading(false);
        }
    };

    const handleSubmitForReview = async () => {
        if (!selectedHomework) return;
        if (!submissionText.trim() && attachments.length === 0) {
            alert('Добавьте текст или вложение перед отправкой');
            return;
        }
        setSubmitLoading(true);
        try {
            await studentAPI.saveHomeworkSubmission(selectedHomework.id, {
                submission_text: submissionText,
                attachments
            });
            await studentAPI.submitHomework(selectedHomework.id);
            await loadSubmission(selectedHomework.id);
        } catch (e) {
            console.error(e);
            alert(e.response?.data?.error || 'Не удалось отправить на проверку');
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleFileSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !selectedHomework) return;
        e.target.value = '';
        setUploadingFile(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await studentAPI.uploadHomeworkAttachment(selectedHomework.id, formData);
            setAttachments(prev => [...prev, { name: res.data.name || file.name, url: res.data.url }]);
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.error || 'Ошибка загрузки файла');
        } finally {
            setUploadingFile(false);
        }
    };

    const removeAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    if (loading) {
        return (
            <main className="student-content">
                <div className="loading">Загрузка...</div>
            </main>
        );
    }

    if (selectedHomework) {
        return (
            <main className="student-content" style={{ padding: '30px' }}>
                <div className="homework-detail-page">
                    <button 
                        onClick={handleCloseHomework}
                        className="btn-back-homework"
                    >
                        ← Назад к списку
                    </button>

                    <div className="homework-detail-card">
                        <div className="homework-detail-header">
                            <h1>{selectedHomework.title}</h1>
                            <div className="homework-meta">
                                <span className="homework-date">
                                    <FiClock />
                                    {new Date(selectedHomework.created_at).toLocaleDateString('ru-RU', {
                                        day: 'numeric',
                                        month: 'long',
                                        year: 'numeric'
                                    })}
                                </span>
                                <span className="homework-author">
                                    Преподаватель: {selectedHomework.created_by_name}
                                </span>
                            </div>
                        </div>

                        <div 
                            className="homework-detail-content"
                            dangerouslySetInnerHTML={{
                                __html: prepareHomeworkHtml(selectedHomework.description, 'student')
                            }}
                        />

                        {Boolean(selectedHomework.is_read) && selectedHomework.read_at && (
                            <div className="homework-read-status">
                                <FiCheckCircle />
                                Прочитано {new Date(selectedHomework.read_at).toLocaleString('ru-RU')}
                            </div>
                        )}

                        <div className="homework-my-response">
                            <h3>Мой ответ</h3>
                            {submissionLoading ? (
                                <div className="homework-response-loading">Загрузка ответа...</div>
                            ) : (
                                <>
                                    <textarea
                                        className="homework-response-text"
                                        placeholder="Введите текст ответа..."
                                        value={submissionText}
                                        onChange={(e) => setSubmissionText(e.target.value)}
                                        disabled={submission?.status === 'submitted' || submission?.status === 'reviewed'}
                                        rows={6}
                                    />
                                    <div className="homework-attachments">
                                        <span className="homework-attachments-label">
                                            <FiPaperclip /> Вложения:
                                        </span>
                                        <ul className="homework-attachments-list">
                                            {attachments.map((a, i) => (
                                                <li key={i}>
                                                    <a href={`${API_ORIGIN}${a.url}`} target="_blank" rel="noopener noreferrer">
                                                        {a.name || 'Файл'}
                                                    </a>
                                                    {(submission?.status === 'pending' || !submission) && (
                                                        <button
                                                            type="button"
                                                            className="homework-attachment-remove"
                                                            onClick={() => removeAttachment(i)}
                                                            aria-label="Удалить"
                                                        >
                                                            <FiX />
                                                        </button>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                        {(submission?.status === 'pending' || !submission) && (
                                            <label className="homework-attachment-add">
                                                <input
                                                    type="file"
                                                    hidden
                                                    onChange={handleFileSelect}
                                                    disabled={uploadingFile}
                                                />
                                                {uploadingFile ? 'Загрузка...' : '+ Добавить файл'}
                                            </label>
                                        )}
                                    </div>
                                    {submission?.status === 'submitted' && (
                                        <div className="homework-response-status submitted">
                                            Отправлено на проверку {submission.submitted_at && new Date(submission.submitted_at).toLocaleString('ru-RU')}
                                        </div>
                                    )}
                                    {submission?.status === 'reviewed' && submission?.teacher_comment && (
                                        <div className="homework-teacher-comment">
                                            <strong>Комментарий преподавателя:</strong>
                                            <p>{submission.teacher_comment}</p>
                                        </div>
                                    )}
                                    {submission?.status === 'reviewed' && submission?.teacher_rating !== null && submission?.teacher_rating !== undefined && (
                                        <div className="homework-teacher-rating">
                                            <strong>Оценка:</strong> {submission.teacher_rating}/10
                                        </div>
                                    )}
                                    {(submission?.status === 'pending' || !submission) && (
                                        <div className="homework-response-actions">
                                            <button
                                                type="button"
                                                className="btn-homework-draft"
                                                onClick={handleSaveDraft}
                                                disabled={saveDraftLoading}
                                            >
                                                <FiSave /> {saveDraftLoading ? 'Сохранение...' : 'Сохранить черновик'}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn-homework-submit"
                                                onClick={handleSubmitForReview}
                                                disabled={submitLoading}
                                            >
                                                <FiSend /> {submitLoading ? 'Отправка...' : 'Отправить на проверку'}
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </main>
        );
    }

    return (
        <main className="student-content" style={{ padding: '30px' }}>
            <div className="student-homework-page">
                <div className="page-header">
                    <h1>Домашние задания</h1>
                    <p>Все задания от преподавателя</p>
                </div>

                {homework.length === 0 ? (
                    <div className="empty-homework">
                        <FiBook size={64} color="#ccc" />
                        <h2>Пока нет домашних заданий</h2>
                        <p>Когда преподаватель отправит вам задание, оно появится здесь</p>
                    </div>
                ) : (
                    <div className="homework-grid">
                        {homework.map(hw => (
                            <div 
                                key={hw.id} 
                                className={`homework-card-student ${!hw.is_read ? 'unread' : ''}`}
                                onClick={() => handleOpenHomework(hw)}
                            >
                                {!hw.is_read && (
                                    <span className="new-badge">Новое</span>
                                )}
                                
                                <h3>{hw.title}</h3>
                                
                                <div 
                                    className="homework-preview"
                                    onClick={(event) => {
                                        if (event.target.closest('a')) {
                                            event.stopPropagation();
                                        }
                                    }}
                                    dangerouslySetInnerHTML={{ 
                                        __html: prepareHomeworkHtml(
                                            hw.description.substring(0, 120) + (hw.description.length > 120 ? '...' : ''),
                                            'student'
                                        )
                                    }}
                                />

                                <div className="homework-card-footer-student">
                                    <span className="homework-date-small">
                                        <FiClock />
                                        {new Date(hw.created_at).toLocaleDateString('ru-RU')}
                                    </span>
                                    {hw.submission_status === 'reviewed' && hw.submission_rating !== null && hw.submission_rating !== undefined && (
                                        <span className="homework-rating-inline">Оценка: {hw.submission_rating}/10</span>
                                    )}
                                    {Boolean(hw.is_read) && (
                                        <span className="read-indicator">
                                            <FiCheckCircle />
                                            Прочитано
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </main>
    );
};

export default StudentHomework;
